"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  Upload,
  FileVideo,
  Play,
  Pause,
  Scissors,
  Download,
  AlertCircle,
  RefreshCw,
  X,
  CheckCircle,
} from "lucide-react";

interface VideoState {
  file: File | null;
  url: string | null;
  duration: number;
  currentTime: number;
  isPlaying: boolean;
  startTime: number;
  endTime: number;
  trimmedUrl: string | null;
  thumbnails: string[];
}

const VideoTrimmer = () => {
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [videoState, setVideoState] = useState<VideoState>({
    file: null,
    url: null,
    duration: 0,
    currentTime: 0,
    isPlaying: false,
    startTime: 0,
    endTime: 0,
    trimmedUrl: null,
    thumbnails: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [isTrimming, setIsTrimming] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [showErrorPopup, setShowErrorPopup] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);

  // Smooth dragging with direct updates
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!timelineRef.current) return;
      
      const rect = timelineRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const percentage = Math.max(0, Math.min(x / rect.width, 1));
      const newTime = percentage * videoState.duration;

      if (isDragging === 'start') {
        const clampedTime = Math.max(0, Math.min(newTime, videoState.endTime - 0.1));
        setVideoState(prev => ({ ...prev, startTime: clampedTime }));
        if (videoRef.current) {
          videoRef.current.currentTime = clampedTime;
        }
      } else if (isDragging === 'end') {
        const clampedTime = Math.min(videoState.duration, Math.max(newTime, videoState.startTime + 0.1));
        setVideoState(prev => ({ ...prev, endTime: clampedTime }));
        if (videoRef.current) {
          videoRef.current.currentTime = clampedTime;
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(null);
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, videoState.duration, videoState.startTime, videoState.endTime]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = (file: File) => {
    const validVideoTypes = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/x-matroska", "video/webm"];
    const validExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
    const maxFileSize = 1024 * 1024 * 1024; // 1GB in bytes

    const fileName = file.name?.toLowerCase() || "";
    const fileExt = fileName.substring(fileName.lastIndexOf("."));

    const isValidMime = validVideoTypes.includes(file.type);
    const isValidExt = validExtensions.includes(fileExt);
    const isValidSize = file.size <= maxFileSize;

    if (file && (isValidMime || isValidExt) && isValidSize) {
      const url = URL.createObjectURL(file);
      setVideoState(prev => ({
        ...prev,
        file,
        url,
        startTime: 0,
        endTime: 0,
        trimmedUrl: null,
      }));
      setError(null);
    } else if (!isValidSize) {
      setError("File size too large. Please upload a video file smaller than 1GB.");
    } else {
      setError("Invalid file type. Please upload a valid video file (.MP4, .MOV, .AVI, .MKV, or .WEBM).");
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      const duration = videoRef.current.duration;
      setVideoState(prev => ({
        ...prev,
        duration,
        startTime: 0,
        endTime: duration,
      }));
      
      // Generate thumbnails after metadata loads
      generateThumbnails();
    }
  };

  const generateThumbnails = async () => {
    if (!videoRef.current || !videoState.url) return;

    const video = videoRef.current;
    const duration = video.duration;
    const thumbnailCount = 20; // Number of thumbnails to generate
    const thumbnails: string[] = [];

    // Create a canvas to capture frames
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 160; // Thumbnail width
    canvas.height = 90; // Thumbnail height

    for (let i = 0; i < thumbnailCount; i++) {
      const time = (duration / thumbnailCount) * i;
      
      try {
        video.currentTime = time;
        await new Promise(resolve => {
          video.onseeked = resolve;
        });

        // Draw the current frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to data URL
        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.7);
        thumbnails.push(thumbnailUrl);
      } catch (error) {
        console.error('Error generating thumbnail:', error);
      }
    }

    // Reset video to current time
    video.currentTime = videoState.currentTime;

    setVideoState(prev => ({
      ...prev,
      thumbnails,
    }));
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !isDragging) {
      const currentTime = videoRef.current.currentTime;
      setVideoState(prev => ({
        ...prev,
        currentTime,
      }));

      // Auto-pause at end time
      if (currentTime >= videoState.endTime && videoState.isPlaying) {
        videoRef.current.pause();
        setVideoState(prev => ({ ...prev, isPlaying: false }));
      }

      // Keep current time within selected range
      if (currentTime < videoState.startTime) {
        videoRef.current.currentTime = videoState.startTime;
        setVideoState(prev => ({ ...prev, currentTime: videoState.startTime }));
      }
    }
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoState.isPlaying) {
        videoRef.current.pause();
      } else {
        // If at or past end time, reset to start time
        if (videoRef.current.currentTime >= videoState.endTime || videoRef.current.currentTime < videoState.startTime) {
          videoRef.current.currentTime = videoState.startTime;
        }
        videoRef.current.play();
      }
      setVideoState(prev => ({
        ...prev,
        isPlaying: !prev.isPlaying,
      }));
    }
  };


  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!timelineRef.current || !videoRef.current || isDragging) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const percentage = Math.max(0, Math.min(x / rect.width, 1));
    const newTime = percentage * videoState.duration;
    
    videoRef.current.currentTime = newTime;
    setVideoState(prev => ({ ...prev, currentTime: newTime }));
  };

  const handleStartMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragging('start');
    if (videoRef.current) {
      videoRef.current.pause();
      setVideoState(prev => ({ ...prev, isPlaying: false }));
    }
  };

  const handleEndMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragging('end');
    if (videoRef.current) {
      videoRef.current.pause();
      setVideoState(prev => ({ ...prev, isPlaying: false }));
    }
  };

  const formatTime = (time: number) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    const ms = Math.floor((time % 1) * 1000);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0').slice(0, 2)}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0').slice(0, 2)}`;
  };

  const handleTrim = async () => {
    if (!videoState.file || !videoRef.current) return;

    setIsTrimming(true);
    setIsUploading(true);
    setIsProcessing(false);
    setUploadProgress(0);
    setError(null);
    setShowErrorPopup(false);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', videoState.file);
      formData.append('startTime', videoState.startTime.toString());
      formData.append('endTime', videoState.endTime.toString());

      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);

          if (percentComplete === 100) {
            setIsUploading(false);
            setIsProcessing(true);
          }
        }
      };

      // Handle the response
      const response = await new Promise<Response>((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Create a Response object from the XMLHttpRequest
            const response = new Response(xhr.responseText, {
              status: xhr.status,
              statusText: xhr.statusText,
              headers: new Headers({
                'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json'
              })
            });
            resolve(response);
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error occurred'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));

        xhr.open('POST', process.env.NEXT_PUBLIC_UPLOAD_BACKEND_API_URL + 'tools/please/trim');
        xhr.send(formData);
      });

      if (!response.ok) {
        let errorMessage = "Video trimming failed. Please try again.";

        switch (response.status) {
          case 422:
            errorMessage = "Invalid file format or corrupted file. Please check your video file.";
            break;
          case 500:
            errorMessage = "Server error occurred. Please try again later.";
            break;
          case 413:
            errorMessage = "File too large. Please upload a smaller video file.";
            break;
          case 429:
            errorMessage = "Too many requests. Please wait a moment and try again.";
            break;
          default:
            errorMessage = `Error ${response.status}: ${response.statusText || "Unknown error occurred"}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.output) {
        throw new Error("No output received from server. Please try again.");
      }

      // Update state with the trimmed video URL from result.output
      setVideoState(prev => ({
        ...prev,
        trimmedUrl: data.output,
      }));
      
      setIsTrimming(false);
      setIsUploading(false);
      setIsProcessing(false);
      setUploadProgress(0);
      
    } catch (error: any) {
      console.error('Error trimming video:', error);
      setIsTrimming(false);
      setIsUploading(false);
      setIsProcessing(false);
      setUploadProgress(0);
      setError(error.message || 'Failed to trim video. Please try again.');
      setShowErrorPopup(true);
    }
  };

  const handleRetry = () => {
    setError(null);
    setShowErrorPopup(false);
    handleTrim();
  };

  const closeErrorPopup = () => {
    setShowErrorPopup(false);
  };

  const resetVideo = () => {
    if (videoState.url) {
      URL.revokeObjectURL(videoState.url);
    }
    if (videoState.trimmedUrl && videoState.trimmedUrl !== videoState.url) {
      URL.revokeObjectURL(videoState.trimmedUrl);
    }
    setVideoState({
      file: null,
      url: null,
      duration: 0,
      currentTime: 0,
      isPlaying: false,
      startTime: 0,
      endTime: 0,
      trimmedUrl: null,
      thumbnails: [],
    });
    setError(null);
    setIsTrimming(false);
    setIsUploading(false);
    setIsProcessing(false);
    setUploadProgress(0);
    setShowErrorPopup(false);
  };

  const downloadTrimmedVideo = () => {
    if (videoState.url && videoState.file) {
      const link = document.createElement('a');
      link.href = videoState.url;
      link.download = `trimmed_${videoState.file.name}`;
      link.click();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-indigo-100 py-12 px-4">
      {/* Error Popup */}
      {showErrorPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
            <button
              onClick={closeErrorPopup}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>

              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Trimming Failed
              </h3>

              <p className="text-gray-600 mb-6">
                {error}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={closeErrorPopup}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRetry}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-2 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4">
            Video Trimmer Pro
          </h1>
          <p className="text-gray-600 text-lg">
            Upload, trim, and download your videos with precision
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-center gap-3 text-red-700 mb-3">
              <AlertCircle className="w-5 h-5" />
              <span className="font-semibold">Error</span>
            </div>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/50 p-8">
          {!videoState.file ? (
            <div
              className={`border-3 border-dashed rounded-2xl p-12 text-center transition-all duration-300 cursor-pointer ${
                isDragOver
                  ? "border-purple-500 bg-purple-50 scale-105"
                  : "border-gray-300 hover:border-purple-400 hover:bg-gray-50"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".mp4,.mov,.avi,.mkv,.webm,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm"
                onChange={handleFileInputChange}
                className="hidden"
              />

              <div className="mx-auto w-24 h-24 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
                <Upload className="w-12 h-12 text-white" />
              </div>

              <h3 className="text-2xl font-semibold text-gray-800 mb-2">
                Drop your video file here
              </h3>
              <p className="text-gray-600 mb-4">
                or click to browse from your device
              </p>

              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {[".mp4", ".avi", ".mov", ".mkv", ".webm"].map((format) => (
                  <span
                    key={format}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
                  >
                    {format}
                  </span>
                ))}
              </div>

              <button className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg">
                Choose Video File
              </button>
            </div>
          ) : videoState.trimmedUrl ? (
            /* Show result with video and actions side by side */
            <div className="py-8">
              {/* Success Header */}
              <div className="text-center mb-10">
                <div className="relative inline-block">
                  <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-400 rounded-full animate-pulse"></div>
                </div>
                <h4 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">
                  Trimming Complete!
                </h4>
                <p className="text-gray-600 text-lg max-w-md mx-auto">
                  Your video has been successfully trimmed and is ready for download
                </p>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
                {/* Left Side - Video Preview */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                      <FileVideo className="w-4 h-4 text-white" />
                    </div>
                    <h5 className="text-xl font-semibold text-gray-800">Video Preview</h5>
                  </div>
                  
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-indigo-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                    <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl border border-gray-200/20">
                      <video
                        src={videoState.trimmedUrl}
                        controls
                        className="w-full h-auto max-h-96"
                        preload="metadata"
                      />
                    </div>
                  </div>
                </div>

                {/* Right Side - Action Buttons */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                      <Download className="w-4 h-4 text-white" />
                    </div>
                    <h5 className="text-xl font-semibold text-gray-800">Quick Actions</h5>
                  </div>
                  
                  {/* Info Text */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200/50">
                    <p className="text-sm text-green-700 font-medium text-center">
                      Your trimmed video is processed and ready. Click download to save it to your device.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {/* Primary Action - Download */}
                    <a
                      href={`${videoState.trimmedUrl}?utm_source=video_trimmer&utm_medium=download_button&utm_campaign=trim_complete&utm_content=primary_action`}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-5 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-lg flex items-center justify-center gap-3 text-lg relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                      <Download className="w-6 h-6 relative z-10" />
                      <span className="relative z-10">Download Trimmed Video</span>
                    </a>

                    {/* Secondary Action - Add AI Captions */}
                    <a
                      href="/signup?utm_source=video_trimmer&utm_medium=signup_button&utm_campaign=trim_complete&utm_content=ai_captions"
                      className="group w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl shadow-lg flex items-center justify-center gap-3 relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                      <Scissors className="w-5 h-5 relative z-10" />
                      <span className="relative z-10">Add AI Captions</span>
                    </a>

                    {/* Tertiary Action - Trim Another Video */}
                    <button
                      onClick={() => {
                        // Track the action before resetting
                        if (typeof window !== 'undefined' && window.gtag) {
                          window.gtag('event', 'click', {
                            event_category: 'video_trimmer',
                            event_label: 'trim_another_video',
                            value: 1
                          });
                        }
                        resetVideo();
                      }}
                      className="group w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-8 py-4 rounded-2xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl shadow-lg flex items-center justify-center gap-3 relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                      <RefreshCw className="w-5 h-5 relative z-10" />
                      <span className="relative z-10">Trim Another Video</span>
                    </button>
                  </div>

                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Video Info Header */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl border border-purple-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                    <FileVideo className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">
                      {videoState.file.name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      Duration: {formatTime(videoState.duration)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={resetVideo}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Video Player or Processing */}
              {!isTrimming ? (
                <div className="relative bg-black rounded-xl overflow-hidden">
                  <video
                    ref={videoRef}
                    src={videoState.url || undefined}
                    onLoadedMetadata={handleVideoLoadedMetadata}
                    onTimeUpdate={handleTimeUpdate}
                    className="w-full h-auto max-h-96"
                    preload="metadata"
                  />
                  
                  {/* Play/Pause Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <button
                      onClick={togglePlayPause}
                      className="pointer-events-auto bg-black/50 hover:bg-black/70 text-white p-6 rounded-full transition-all duration-300 transform hover:scale-110"
                    >
                      {videoState.isPlaying ? (
                        <Pause className="w-10 h-10" />
                      ) : (
                        <Play className="w-10 h-10 ml-1" />
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  {isUploading ? (
                    <div>
                      <div className="inline-flex items-center gap-3 text-purple-600 mb-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                        <span className="font-semibold">
                          Uploading... {uploadProgress}%
                        </span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-3 overflow-hidden mb-2">
                        <div
                          className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600">
                        {uploadProgress < 100 ? "Please wait while we upload your file..." : "Upload complete, starting trimming..."}
                      </p>
                    </div>
                  ) : isProcessing ? (
                    <div>
                      <div className="inline-flex items-center gap-3 text-purple-600 mb-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                        <span className="font-semibold">
                          Processing your video...
                        </span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-3 overflow-hidden mb-2">
                        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full animate-pulse w-full"></div>
                      </div>
                      <p className="text-sm text-gray-600">
                        Trimming your video, this may take a few moments...
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="inline-flex items-center gap-3 text-purple-600">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                        <span className="font-semibold">
                          Initializing...
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}


              {/* Timeline Section - only show when not processing */}
              {!isTrimming && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Trim Video</h3>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={togglePlayPause}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md"
                    >
                      {videoState.isPlaying ? (
                        <>
                          <Pause className="w-4 h-4" />
                          <span className="text-sm font-medium">Pause</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 ml-0.5" />
                          <span className="text-sm font-medium">Play</span>
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={handleTrim}
                      disabled={isTrimming}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white rounded-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 shadow-md disabled:cursor-not-allowed"
                    >
                      {isTrimming ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span className="text-sm font-medium">Processing...</span>
                        </>
                      ) : (
                        <>
                          <Scissors className="w-4 h-4" />
                          <span className="text-sm font-medium">Export</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Time Display */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-xs text-gray-600 mb-1">Start</div>
                    <div className="text-base font-mono font-bold text-purple-600">{formatTime(videoState.startTime)}</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-xs text-gray-600 mb-1">Current</div>
                    <div className="text-base font-mono font-bold text-indigo-600">{formatTime(videoState.currentTime)}</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg border border-gray-200">
                    <div className="text-xs text-gray-600 mb-1">End</div>
                    <div className="text-base font-mono font-bold text-purple-600">{formatTime(videoState.endTime)}</div>
                  </div>
                </div>

                {/* Custom Timeline */}
                <div className="relative select-none">
                  <div
                    ref={timelineRef}
                    onClick={handleTimelineClick}
                    className="relative h-20 bg-gradient-to-b from-gray-200 to-gray-300 rounded-lg cursor-pointer overflow-hidden shadow-inner"
                  >
                    {/* Video Thumbnails */}
                    {videoState.thumbnails.length > 0 && (
                      <div className="absolute inset-0 flex">
                        {videoState.thumbnails.map((thumbnail, index) => (
                          <div
                            key={index}
                            className="h-full border-r border-gray-400"
                            style={{
                              width: `${100 / videoState.thumbnails.length}%`,
                              backgroundImage: `url(${thumbnail})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                        ))}
                      </div>
                    )}
                    {/* Unselected regions */}
                    <div
                      className="absolute top-0 bottom-0 bg-gray-400/30"
                      style={{
                        left: 0,
                        width: `${(videoState.startTime / videoState.duration) * 100}%`,
                      }}
                    />
                    <div
                      className="absolute top-0 bottom-0 bg-gray-400/30"
                      style={{
                        left: `${(videoState.endTime / videoState.duration) * 100}%`,
                        right: 0,
                      }}
                    />

                    {/* Selected Range */}
                    <div
                      className="absolute top-0 bottom-0 bg-gradient-to-r from-purple-500/40 to-indigo-500/40 border-y-2 border-purple-500"
                      style={{
                        left: `${(videoState.startTime / videoState.duration) * 100}%`,
                        width: `${((videoState.endTime - videoState.startTime) / videoState.duration) * 100}%`,
                      }}
                    />

                    {/* Current Time Indicator */}
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 pointer-events-none"
                      style={{
                        left: `${(videoState.currentTime / videoState.duration) * 100}%`,
                        // Only show tracker within the selected range
                        display: videoState.currentTime >= videoState.startTime && videoState.currentTime <= videoState.endTime ? 'block' : 'none',
                      }}
                    >
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full shadow-md" />
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rounded-full shadow-md" />
                    </div>

                    {/* Start Handle */}
                    <div
                      onMouseDown={handleStartMouseDown}
                      className={`absolute top-0 bottom-0 w-4 cursor-ew-resize z-40 group ${
                        isDragging === 'start' ? 'bg-purple-700 scale-105' : 'bg-purple-600 hover:bg-purple-700 hover:scale-105 transition-all duration-75 ease-out'
                      }`}
                      style={{
                        left: `calc(${(videoState.startTime / videoState.duration) * 100}% - 8px)`,
                      }}
                    >
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full opacity-80" />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-purple-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        {formatTime(videoState.startTime)}
                      </div>
                    </div>

                    {/* End Handle */}
                    <div
                      onMouseDown={handleEndMouseDown}
                      className={`absolute top-0 bottom-0 w-4 cursor-ew-resize z-40 group ${
                        isDragging === 'end' ? 'bg-purple-700 scale-105' : 'bg-purple-600 hover:bg-purple-700 hover:scale-105 transition-all duration-75 ease-out'
                      }`}
                      style={{
                        left: `calc(${(videoState.endTime / videoState.duration) * 100}% - 8px)`,
                      }}
                    >
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-12 bg-white rounded-full opacity-80" />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-purple-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                        {formatTime(videoState.endTime)}
                      </div>
                    </div>
                  </div>

                  {/* Timeline Labels */}
                  <div className="flex justify-between mt-2 text-xs text-gray-600 font-mono">
                    <span>0:00.00</span>
                    <span>{formatTime(videoState.duration)}</span>
                  </div>
                </div>

                <div className="mt-4 text-xs text-gray-500 text-center">
                  Drag the handles to set start and end points
                </div>
                </div>
              )}


              {/* Show error state inline */}
              {error && !showErrorPopup && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center gap-3 text-red-700 mb-3">
                    <AlertCircle className="w-5 h-5" />
                    <span className="font-semibold">Trimming Failed</span>
                  </div>
                  <p className="text-red-600 text-sm mb-3">{error}</p>
                  <button
                    onClick={handleRetry}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 text-sm"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retry Upload
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Trust indicators */}
        <div className="text-center mt-12">
          <p className="text-sm text-gray-500 mb-4">
            Trusted by content creators worldwide
          </p>
          <div className="flex justify-center items-center gap-8 opacity-60">
            <div className="text-xl font-bold text-gray-400">
              1000+ Videos Trimmed
            </div>
            <div className="text-xl font-bold text-gray-400">•</div>
            <div className="text-xl font-bold text-gray-400">100% Free</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoTrimmer;